import { Loc, select } from "gamedata/GameData";
import { Link, useLoaderData } from "react-router";
import { z } from "zod";
import { Breadcrumb } from "~/components/ui/Breadcrumb";
import { Icon } from "~/components/ui/Icon";
import { PageTitle } from "~/components/ui/PageTitle";
import { Table } from "~/components/ui/Table";
import { gameName } from "~/util/common";
import { lstring, Realm, tt, useT, useTurl } from "~/util/Lang";
import { applyPaging, Pager } from "~/util/Listing";
import type { MetaFunc, OurLoaderArgs } from "~/util/Loaders";
import { SortColumnName, useUrlSorting } from "~/util/Sorting";
import { logPageView } from "~/util/stats";
import { homePage } from "~/util/titles";

const PageQuery = z.object({
  p: z.optional(z.string()),
});

async function makeData(la: OurLoaderArgs) {
  // Log
  logPageView(la);
  //
  const t = tt.bind(null, la.realm.lang);
  let all = Array.from(la.gd.skills.values());
  const query = PageQuery.parse(
    Object.fromEntries(la.url.searchParams.entries())
  );
  const [paged, paging] = applyPaging(all, query);

  const skills = select(paged, {
    select: {
      id: true,
      cardName: true,
      icon: true,
      name: t,
      operateType: true,
      levels(x) {
        return x.size;
      },
      enchanting(x) {
        return x.size > 0;
      },
    },
  });

  return { skills, paging };
}

type LoaderData = Awaited<ReturnType<typeof makeData>>;

export const loader = async (la: OurLoaderArgs) => {
  const data = await makeData(la);
  return { ...data, authUser: la.req.sess?.user };
};

export const meta: MetaFunc<LoaderData> = (cx) => {
  return {
    title: `${cx.t({ en: "Skills", ru: "Умения" })} - ${homePage(cx.realm)}`,
    description: getDesc(cx),
    ogDescription: getDesc(cx),
    ogTitle: getOgTitle(cx),
  };
};

export default function SkillsPage() {
  const data = useLoaderData() as LoaderData;
  const t = useT();
  const turl = useTurl();

  const pager = <Pager paging={data.paging} />;
  const urlSorting = useUrlSorting();
  const locsTitle = t({ en: "Skills", ru: "Умения" });
  return (
    <div>
      <div className="content">
        <Breadcrumb items={[{ title: locsTitle, url: turl("/skills") }]} />
        <PageTitle>{locsTitle}</PageTitle>
      </div>
      {pager}
      <Table
        data={data.skills}
        id={(skill) => skill.cardName}
        cols={[
          {
            title: "",
            cell: (skill) => <Icon icon={skill.icon} />,
            thPadding: "0",
            padding: "8px 0 8px 8px",
            width: 32 + 12,
          },
          {
            title: t({ en: "Name", ru: "Название" }),
            cell: (skill) => (
              <>
                <Link to={turl(`/skills/${skill.cardName}:1`)}>
                  {skill.name}
                </Link>
              </>
            ),
          },
          {
            title: t({ en: "Type", ru: "Тип" }),
            cell: (skill) => skill.operateType,
          },
          {
            title: t({ en: "Levels", ru: "Уровней" }),
            cell: (skill) => skill.levels,
          },
          {
            title: t({ en: "Enchanting", ru: "Enchanting" }),
            cell: (skill) => skill.enchanting && "+",
          },
          // {
          //   title: t({ en: "Lvl", ru: "Уровень" }),
          //   cell: (skill) => skill.level
          // },
        ]}
      />
      {pager}
    </div>
  );
}

function getDesc(cx: { realm: Realm; t: (str: lstring) => string }) {
  return cx.t({
    en: `A complete searchable and filterable list of all Skills in ${gameName(
      cx.realm.game
    )}.`,
    ru: `Полный список умений ${gameName(cx.realm.game)}`,
  });
}

function getOgTitle(cx: { realm: Realm; t: (str: lstring) => string }) {
  return cx.t({
    en: `All Skills${gameName(cx.realm.game)}`,
    ru: `Все умения ${gameName(cx.realm.game)}`,
  });
}
